// A chat page's body (D82): the browser must be one of the user's devices to read and write;
// then the conversation is decrypted here (useConversation) and its turns polled while one is
// in flight. Whether to send history with prompts follows the account key's mode.
import { LockKey } from "@phosphor-icons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";

import { fetchAccountKey } from "../account/api";
import { useConversation } from "../conversation/useConversation";
import { type DeviceState, useDevice } from "../device/context";
import { useT } from "../i18n";
import { toast } from "../layout/toast";
import { WidgetCard } from "../ui/Card";
import { EmptyState, ErrorState, Loading } from "../ui/states";
import { E2EEBadge } from "../ui/status";
import { runQuestionsQuery } from "../api/queries";
import { PendingQuestions } from "../tasks/task/PendingQuestions";
import { cancelTurn, turnsQuery } from "./api";
import { ChatPanel } from "./ChatPanel";

type Linked = DeviceState & { kind: "linked" };

const accountKeyQuery = () => ({ queryKey: ["account-key"], queryFn: fetchAccountKey });

interface Copy {
  emptyBody: string;
  waitHint: string;
  /** A task chat whose step's agent waits on questions (S141): that step's run. */
  questionsRun?: string;
}

function LinkedChat({ device, conversationId, emptyBody, waitHint, questionsRun }: Copy & { device: Linked; conversationId: string }) {
  const t = useT().chat;
  const { state, send } = useConversation(device, conversationId);
  // The step's questions are answered above the thread, from the conversation decrypted here.
  const questions = useQuery({ ...runQuestionsQuery(questionsRun ?? ""), enabled: questionsRun !== undefined });
  // `GET /api/runs/{id}/questions` lists only the pending ones.
  const pending = questionsRun ? (questions.data ?? []) : [];
  const turns = useQuery(turnsQuery(conversationId));
  const key = useQuery(accountKeyQuery());
  const queryClient = useQueryClient();
  const cancel = useMutation({
    mutationFn: (turnId: string) => cancelTurn(conversationId, turnId),
    onSuccess: () => {
      toast(t.turn.cancelled);
      void queryClient.invalidateQueries({ queryKey: turnsQuery(conversationId).queryKey });
    },
  });
  if (turns.isError) return <ErrorState title={t.loadFailed} message={turns.error.message} />;
  const panel = (
    <ChatPanel
      conversationId={conversationId}
      state={state}
      send={send}
      turns={turns.data ?? []}
      selfManaged={key.data?.mode !== "managed"}
      emptyBody={emptyBody}
      waitHint={waitHint}
      onCancel={(turnId) => cancel.mutate(turnId)}
    />
  );
  if (pending.length === 0) return panel;
  return (
    <div className="space-y-5">
      <PendingQuestions questions={pending} state={state} access={{ kind: "linked", deviceId: device.device.deviceId, send }} />
      {panel}
    </div>
  );
}

/** The chat card: who may read it, then the thread and composer for a linked browser. */
export function ChatView({ conversationId, emptyBody, waitHint, title, questionsRun }: Copy & { conversationId: string | undefined; title: string }) {
  const t = useT().chat;
  const device = useDevice();
  let body;
  if (device.kind === "loading" || conversationId === undefined) body = <Loading label={t.checkingKeys} />;
  else if (device.kind === "unlinked") {
    body = (
      <EmptyState compact icon={<LockKey weight="duotone" />} title={t.linkThisBrowser}>
        {t.linkFirst} <Link to="/devices">{t.linkThisBrowser}</Link>
      </EmptyState>
    );
  } else body = <LinkedChat key={conversationId} device={device} conversationId={conversationId} emptyBody={emptyBody} waitHint={waitHint} questionsRun={questionsRun} />;
  return (
    <WidgetCard title={title} action={<E2EEBadge />}>
      {body}
    </WidgetCard>
  );
}
